import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import useDebounce from "../../../../hooks/useDebounce";
import { v4 as uuidv4 } from "uuid";
import { getAllParts } from "../../../../redux/partSlice";
import "../styles.css";
import TextboxContentFormatter from "../text-editor/TextboxContentFormatter";
import LexicalTextEditor from "../text-editor/LexicalTextEditor";
import { calculateGroupHeight } from "../../../SectionElements/plugins/utils";
// UI Components
import ExpandCollapseHeader from "./ui/ExpandCollapseHeader";
import PositionControls from "./ui/PositionControls";
import WidthControl from "./ui/WidthControl";
import StrokeSizeControl from "./ui/StrokeSizeControl";
import BackgroundSettings from "./ui/BackgroundSettings";
import FontSizeButtons from "./ui/FontSizeButtons";
import DeleteButton from "./ui/DeleteButton";
import EditContentButton from "./ui/EditContentButton";
import BackButton from "./ui/BackButton";
import SectionButtons from "./ui/SectionButtons";
import EditItemCodeButton from "./ui/EditItemCodeButton";
import QuantityUnitControls from "./ui/QuantityUnitControls";
import PartActionButtons from "./ui/PartActionButtons";
import NavigationToolbar from "./ui/NavigationToolbar";
import RemoveNoteButton from "./ui/RemoveNoteButton";
// Handlers
import {
  createHandlePartChange,
  createHandleHighlightChange,
  createHandleDeleteNotForPart,
} from "./handlers/partHandlers";
import {
  createHandlePrevPart,
  createHandleNextPart,
} from "./handlers/navigationHandlers";
import {
  createHandleQtyChange,
  createHandleUnitChange,
  createHandleRemovePart,
} from "./handlers/qtyUnitHandlers";
import {
  applyFormatToSelection,
  createUpdateFormattedText,
  createHandleListKeyDown,
} from "./handlers/formattingHandlers";
import {
  createHandleColorChange,
  createHandleUnderlineChange,
  createHandleNumberedList,
  createHandleBulletedList,
  createHandleContentHighlightChange,
  createHandleLinkChange,
} from "./handlers/contentFormattingHandlers";
// Utils
import {
  getFontSizeLabel,
  placeCaretAtEnd,
  handleNumberInputWithLimits,
} from "./utils/textUtils";
import {
  getAllPartsFromText,
  getCurrentGlobalIndex,
  extractSelectedPartName,
} from "./utils/partUtils";
import { getFilteredUnits } from "./utils/unitUtils";
import {
  updateTextAndSelectedText,
  updateTextField,
  updateSelectedTextOnly,
} from "./utils/stateUpdateUtils";
import { rgbToHex } from "./utils/colorUtils";
import {
  getCenterX,
  getCenterY,
  getTopLeftX,
  getTopLeftY,
} from "./utils/coordinateUtils";
import { useSelectedTextColor } from "./hooks/useSelectedTextColor";
import { useSelectionSaver } from "./hooks/useSelectionSaver";
import { useUnitTypeDetection } from "./hooks/useUnitTypeDetection";
import { useContentCleanup } from "./hooks/useContentCleanup";
import { createNumberInputHandler } from "./handlers/inputHandlers";
import { createLexicalEditorHandlers } from "./handlers/lexicalEditorHandlers";
import { createAddNoteBelowPart } from "./handlers/noteHandlers";
import {
  createNoteContentChangeHandler,
  createNoteColorChangeHandler,
  createNoteUnderlineChangeHandler,
  createNoteHighlightChangeHandler,
  createNoteLinkChangeHandler,
  getNoteContent,
  shouldSaveNoteHistory,
} from "./handlers/noteEditorHandlers";
import {
  getCanvasStageHeightPx,
  getMainCanvasWidthPx,
  getOffCanvasStageWidthPx,
  getStageHeightPxFromCanvasHeight,
} from "../../utils/mainCanvasLayout";
import { getOffCanvasMainColumnBounds } from "../../utils/boundaryUtils";

const BUTTON_WIDTH = 260;
const BUTTON_HEIGHT = 30;
const BUTTON_COLOR = "#007bff";
const BUTTON_HOVER_COLOR = "#0056b3";
const TEXT_COLOR = "white";
const EDITOR_EDGE_BUFFER_PX = 10;
const EMPTY_TEXT_BOTTOM_CLAMP_SLACK_PX = 2;

// Move isDeselectingRef to the module scope so it can be shared
const isDeselectingRef = { current: false };

function setTextBoxDeselecting(flag) {
  isDeselectingRef.current = flag;
}

const isEffectivelyEmptyText = (textValue) => {
  const raw = textValue == null ? "" : String(textValue);
  const withoutTags = raw.replace(/<[^>]*>/g, "");
  return withoutTags.trim().length === 0;
};

const TextBoxWithButtons = ({
  onClick,
  id,
  setSelectedText,
  setSelectedTextId,
  selectedTextId,
  setPartsModalOpen,
  setTexts,
  text,
  showEditTask,
  showEditNote,
  showQtyUnitOptions: showQtyUnitOptionsProp = undefined,
  setShowQtyUnitOptions: setShowQtyUnitOptionsProp = undefined,
  setShowEditTask,
  setShowEditNote,
  editTextBoxContent,
  setEditTextBoxContent,
  setShowTextBox,
  getTextStyle,
  saveToHistory,
  canvasHeight,
  setRemovedTexts,
  // Add offcanvas text props
  setOffCanvasTexts,
  offCanvasTexts,
  setRemovedOffCanvasTexts,
  // Add texts prop for main canvas reference
  texts,
  // Grouped undo/redo props
  isElementBeingEdited = false,
  setPendingChanges = () => { },
  pendingChangesRef = null, // Add ref for synchronous updates
  // Transformer props for cleanup on delete
  setSelectedObject,
  transformerRef,
  // Callback to track removed part references
  onPartRemoved,
  // Optional: setSelectedObjects for multi-select cleanup
  setSelectedObjects,
}) => {
  const MIN_WIDTH = 40;
  const MAIN_STAGE_WIDTH = getMainCanvasWidthPx();
  const MIN_STROKE_WIDTH = 0;
  const MAX_STROKE_WIDTH = 20;
  const resolveStageHeightPx = (heightValue) => {
    const numeric = Number(heightValue);
    if (Number.isFinite(numeric) && numeric > 100) return numeric;
    return getStageHeightPxFromCanvasHeight(heightValue);
  };
  const canvasPixelHeight = resolveStageHeightPx(canvasHeight);

  const MIN_X = EDITOR_EDGE_BUFFER_PX;
  const MIN_Y = EDITOR_EDGE_BUFFER_PX;

  const [isExpanded, setIsExpanded] = useState(true);
  const contentTextareaRef = useRef(null);

  const taskTextareaRef = useRef(null);
  const noteTextareaRef = useRef(null);
  const [savedNote, setSavedNote] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [fontSize, setFontSize] = useState(getFontSizeLabel(text?.fontSize));

  // Helper function to determine which setter to use based on whether text is in main canvas or off-canvas
  const getTextSetter = useMemo(() => {
    if (!text?.id) return setTexts;

    const isInMainCanvas = texts && texts.some((t) => t.id === text.id);
    const isInOffCanvas =
      offCanvasTexts && offCanvasTexts.some((t) => t.id === text.id);

    return isInOffCanvas ? setOffCanvasTexts : setTexts;
  }, [text?.id, texts, offCanvasTexts, setTexts, setOffCanvasTexts]);

  // 🔄 Keep fontSize in sync when text.fontSize changes
  useEffect(() => {
    if (text?.fontSize) {
      setFontSize(getFontSizeLabel(text.fontSize));
    }
  }, [text?.fontSize]);

  // Determine if text is on off-canvas
  const isTextOnOffCanvas = useMemo(() => {
    if (text?._previewTargetStage === "offCanvas") return true;
    if (text?._previewTargetStage === "main") return false;
    // CRITICAL FIX: Check _isInOffCanvasDuringDrag flag first (for elements being dragged between canvases)
    if (text?._isInOffCanvasDuringDrag === true) return true;
    if (text?._isInOffCanvasDuringDrag === false) return false;
    if (text?.isOffCanvas === true) return true;

    // Fallback: Check if it's in the offCanvasTexts array
    if (offCanvasTexts && offCanvasTexts.some((t) => t.id === text?.id)) return true;

    // Final fallback: Check X position (like RectangleEditor)
    // This catches cases where state arrays might not be updated yet but position is clearly off-canvas
    if (text?.x !== undefined && text?.x >= MAIN_STAGE_WIDTH) return true;

    return false;
  }, [
    text?.id,
    text?._previewTargetStage,
    text?._isInOffCanvasDuringDrag,
    text?.isOffCanvas,
    text?.x,
    offCanvasTexts,
  ]);
  const activeStageWidth = isTextOnOffCanvas
    ? getOffCanvasStageWidthPx()
    : MAIN_STAGE_WIDTH;
  const offCanvasMainZoneStart = isTextOnOffCanvas
    ? getOffCanvasMainColumnBounds(activeStageWidth).mainZoneStart
    : activeStageWidth;
  const usableStageWidth = isTextOnOffCanvas
    ? offCanvasMainZoneStart
    : activeStageWidth;
  const activeStageHeight = isTextOnOffCanvas
    ? getCanvasStageHeightPx()
    : canvasPixelHeight;
  const stageRelativeTextX = (() => {
    const rawX = Number(text?.x);
    if (!Number.isFinite(rawX)) return 0;
    if (isTextOnOffCanvas) {
      if (rawX < 0) {
        return rawX + MAIN_STAGE_WIDTH;
      }
      // If coordinates are in the off-canvas main-column range, fold them back
      // to the editable left strip so width max behaves like main-canvas logic.
      if (rawX > offCanvasMainZoneStart) {
        return rawX - offCanvasMainZoneStart;
      }
    }
    return rawX;
  })();
  const currentTextWidth = Math.max(MIN_WIDTH, Number(text?.width) || MIN_WIDTH);
  const currentTextHeight = Math.max(1, Number(text?.height) || 35);
  const bottomClampSlackPx = isEffectivelyEmptyText(text?.text)
    ? EMPTY_TEXT_BOTTOM_CLAMP_SLACK_PX
    : 0;
  const dynamicMaxX = Math.max(
    MIN_X,
    activeStageWidth - currentTextWidth - EDITOR_EDGE_BUFFER_PX,
  );
  const dynamicMaxY = Math.max(
    MIN_Y,
    activeStageHeight -
      currentTextHeight -
      EDITOR_EDGE_BUFFER_PX +
      bottomClampSlackPx,
  );
  const dynamicMaxWidth = Math.max(
    MIN_WIDTH,
    usableStageWidth -
      Math.max(MIN_X, stageRelativeTextX) -
      EDITOR_EDGE_BUFFER_PX,
  );

  // Local state for input values to avoid immediate updates
  // CRITICAL FIX: Show coordinates relative to the stage the element is currently on
  // If element is on off-canvas stage, show coordinates relative to off-canvas origin
  // If element is on main canvas stage, show coordinates relative to main canvas origin
  const getInitialCoordinates = (text) => {
    if (text?.x !== undefined && text.x < 0) {
      // Element is in off-canvas stage with negative coordinates, convert to positive
      const mainCanvasWidth = MAIN_STAGE_WIDTH;
      return Math.round(text.x + mainCanvasWidth);
    }
    return text?.x !== undefined ? Math.round(text.x) : "";
  };

  const [localInputValues, setLocalInputValues] = useState({
    x: getInitialCoordinates(text),
    y: text?.y !== undefined ? Math.round(text.y) : "",
    width: Math.round(text?.width || MIN_WIDTH),
    strokeWidth: Math.round(text?.strokeWidth ?? 0),
  });

  // Update local state when text changes
  useEffect(() => {
    if (text) {
      // CRITICAL FIX: Detect if text element is in off-canvas stage and convert coordinates
      // If x is negative, it means the element is in off-canvas stage but coordinates are still relative to main canvas
      let displayX = text.x !== undefined ? Math.round(text.x) : "";
      let displayY = text.y !== undefined ? Math.round(text.y) : "";

      // If text has negative x coordinates, it's likely in off-canvas stage
      // Convert to positive coordinates relative to off-canvas stage origin
      if (text.x !== undefined && text.x < 0) {
        // Element is in off-canvas stage, convert coordinates to be relative to off-canvas origin
        // Off-canvas stage starts where main canvas ends (window.innerWidth * 0.45)
        const mainCanvasWidth = MAIN_STAGE_WIDTH;
        displayX = Math.round(text.x + mainCanvasWidth);

      }

      setLocalInputValues({
        x: displayX,
        y: displayY,
        width: Math.round(text.width || MIN_WIDTH),
        strokeWidth: Math.round(text.strokeWidth ?? 0),
      });
    }
  }, [text]);

  const [showQtyUnitOptionsLocal, setShowQtyUnitOptionsLocal] = useState(false);
  const showQtyUnitOptions =
    showQtyUnitOptionsProp !== undefined
      ? showQtyUnitOptionsProp
      : showQtyUnitOptionsLocal;
  const setShowQtyUnitOptions =
    setShowQtyUnitOptionsProp || setShowQtyUnitOptionsLocal;
  const selectedColor = useSelectedTextColor({
    textId: text?.id,
    editTextBoxContent,
    showEditNote,
    showEditTask,
    isExpanded,
  });
  const { units } = useSelector((state) => state.units);
  const noteFontSize = getTextStyle(text, "note").fontSize || 14;

  const taskFontSize =
    text?.fontSize || getTextStyle(text, "task").fontSize || 14;
  const contentFontSize =
    text?.fontSize || getTextStyle(text, "content").fontSize || 14;
  const [showPartNoteEdit, setShowPartNoteEdit] = useState(false);
  const { parts } = useSelector((state) => state.parts);
  const dispatch = useDispatch();
  const selectedContent = text?.selectedContent;

  const selectedPartName = extractSelectedPartName(selectedContent);

  useEffect(() => {
    const match = selectedPartName?.match(/\((\d+)\)/);
    const extractedPartNumber = match ? match[1] : "";

    dispatch(
      getAllParts({ limit: 100, offset: 0, partNumber: extractedPartNumber })
    );
  }, [selectedPartName]);

  // Track previous selected part ID to detect new part additions
  const prevSelectedPartIdRef = useRef(null);
  const [shouldAutoFocusQty, setShouldAutoFocusQty] = useState(false);

  // Check if the selected part has a note and set showPartNoteEdit accordingly
  useEffect(() => {
    if (text?.selectedContent?.type === "part" && text?.partsByType) {
      let hasNote = false;
      const selectedId = text?.selectedContent?.id;

      Object.entries(text.partsByType || {}).forEach(([_, typeData]) => {
        typeData.parts.forEach((partObj) => {
          // Skip null or undefined partObj entries
          if (!partObj) return;

          if (partObj?.id === selectedId && partObj?.note) {
            hasNote = true;
          }
        });
      });

      setShowPartNoteEdit(hasNote);

      // Detect if this is a newly added part (part was just added and selected)
      // A new part is detected when:
      // 1. selectedContent.type is "part"
      // 2. The selected part ID is different from the previous one
      // 3. The part has empty qty (newly added parts start with empty qty)
      // 4. The qty/unit section is not already showing (to avoid re-triggering)
      // 5. NOT in edit note mode - when editing a note we should never show edit item code section
      const selectedPart = Object.values(text.partsByType || {})
        .flatMap((typeData) => typeData.parts || [])
        .find((partObj) => partObj?.id === selectedId);

      const isNewPart =
        selectedPart &&
        selectedPart.qty === "" &&
        prevSelectedPartIdRef.current !== selectedId &&
        !showQtyUnitOptions && // Only trigger if qty/unit section is not already showing
        !showEditNote; // Never show when editing note (double-click on part note)

      if (isNewPart) {
        // Show qty/unit section and focus qty input
        setShowQtyUnitOptions(true);
        setShouldAutoFocusQty(true);
        // Reset after a short delay to allow focus to work
        setTimeout(() => {
          setShouldAutoFocusQty(false);
        }, 200);
      }

      // Update previous selected part ID
      prevSelectedPartIdRef.current = selectedId;
    } else {
      setShowPartNoteEdit(false);
      prevSelectedPartIdRef.current = null;
    }
  }, [
    text?.selectedContent?.id,
    text?.selectedContent?.type,
    text?.partsByType,
    showQtyUnitOptions,
    showEditNote,
  ]);

  // Content cleanup effect
  useContentCleanup({ text, setTexts: getTextSetter, setSelectedText });

  // Automatically continue bullets or numbering on Enter
  const handleListKeyDown = createHandleListKeyDown(placeCaretAtEnd);

  // Handle part changes for each category
  const handlePartChange = createHandlePartChange({
    text,
    selectedTextId,
    setTexts: getTextSetter,
    setSelectedText,
    isElementBeingEdited,
    setPendingChanges,
  });

  const handleHighlightChange = createHandleHighlightChange({
    text,
    setTexts: getTextSetter,
    setSelectedText,
    saveToHistory,
    isElementBeingEdited,
    setPendingChanges,
  });

  const handleDeleteNotForPart = createHandleDeleteNotForPart({
    text,
    setTexts: getTextSetter,
    setSelectedText,
    saveToHistory,
    isElementBeingEdited,
    setPendingChanges,
  });

  const addNoteBelowPart = createAddNoteBelowPart({
    text,
    setTexts: getTextSetter,
    setSelectedText,
    saveToHistory,
    isElementBeingEdited,
    setPendingChanges,
  });

  const allParts = getAllPartsFromText(text);
  const currentGlobalIndex = getCurrentGlobalIndex(
    allParts,
    text?.selectedContent?.id
  );

  // Unit type detection hook
  const { selectedPartUnitTypeId, unitsUpdateTrigger } = useUnitTypeDetection({
    text,
    selectedPartName,
    allParts,
    currentGlobalIndex,
  });

  // Memoize filtered units to ensure they update when selectedPartUnitTypeId changes
  const filteredUnits = useMemo(() => {
    const selectedPartId =
      text?.selectedContent?.part?.id || text?.selectedContent?.id;
    return getFilteredUnits({
      selectedPartUnitTypeId,
      units,
      text,
      selectedPartId,
    });
  }, [
    selectedPartUnitTypeId,
    unitsUpdateTrigger,
    units,
    text?.selectedContent,
  ]);

  // Handle Prev/Next Part functions - wrapped to prevent auto-showing qty/unit panel
  const baseHandlePrevPart = createHandlePrevPart({
    text,
    allParts,
    currentGlobalIndex,
    setTexts: getTextSetter,
    setSelectedText,
  });

  const baseHandleNextPart = createHandleNextPart({
    text,
    allParts,
    currentGlobalIndex,
    setTexts: getTextSetter,
    setSelectedText,
  });

  // Wrap handlers to ensure Edit/Remove buttons show instead of qty/unit panel
  const handlePrevPart = () => {
    if (currentGlobalIndex > 0) {
      const prevPart = allParts[currentGlobalIndex - 1];
      // Update ref before selecting to prevent useEffect from detecting as new part
      prevSelectedPartIdRef.current = prevPart.id;
      // Hide qty/unit panel to show Edit/Remove buttons
      setShowQtyUnitOptions(false);
      // Close content editor if open
      setEditTextBoxContent(false);
      // Call the base handler
      baseHandlePrevPart();
    }
  };

  const handleNextPart = () => {
    if (currentGlobalIndex < allParts.length - 1) {
      const nextPart = allParts[currentGlobalIndex + 1];
      // Update ref before selecting to prevent useEffect from detecting as new part
      prevSelectedPartIdRef.current = nextPart.id;
      // Hide qty/unit panel to show Edit/Remove buttons
      setShowQtyUnitOptions(false);
      // Close content editor if open
      setEditTextBoxContent(false);
      // Call the base handler
      baseHandleNextPart();
    }
  };

  const hasPrevPart = currentGlobalIndex > 0;
  const hasNextPart = currentGlobalIndex < allParts.length - 1;

  // Handle Quantity Change
  const handleQtyChange = createHandleQtyChange({
    text,
    selectedTextId,
    filteredUnits,
    setTexts: getTextSetter,
    setSelectedText,
    handlePartChange,
    saveToHistory,
    isElementBeingEdited,
    setPendingChanges,
  });

  const handleUnitChange = createHandleUnitChange({
    text,
    selectedTextId,
    filteredUnits,
    setTexts: getTextSetter,
    setSelectedText,
    handlePartChange,
    saveToHistory,
    isElementBeingEdited,
    setPendingChanges,
  });

  // Handle Remove Part
  const handleRemovePart = createHandleRemovePart({
    text,
    setTexts: getTextSetter,
    setSelectedText,
    setEditTextBoxContent,
    setShowEditNote,
    setShowEditTask,
    setShowQtyUnitOptions,
    saveToHistory,
    isElementBeingEdited,
    setPendingChanges,
    onPartRemoved, // Pass the callback from props
  });

  // ----- early exit after hooks to keep hook call order -----
  // if (text === null) return null; // removed to keep hooks order

  // The problematic code that was causing infinite loops has been removed

  const updateFormattedText = createUpdateFormattedText({
    setTexts: getTextSetter,
    setSelectedText,
    saveToHistory,
    isElementBeingEdited,
    setPendingChanges,
  });

  // Create content formatting handlers
  const handleColorChange = createHandleColorChange({
    text,
    setTexts: getTextSetter,
    setSelectedText,
    saveToHistory,
    applyFormatToSelection,
  });

  const handleUnderlineChange = createHandleUnderlineChange({
    text,
    setTexts: getTextSetter,
    setSelectedText,
    saveToHistory,
    updateFormattedText,
  });

  const handleNumberedList = createHandleNumberedList({
    text,
    updateFormattedText,
  });

  const handleBulletedList = createHandleBulletedList({
    text,
    updateFormattedText,
  });

  const handleContentHighlightChange = createHandleContentHighlightChange({
    text,
    setTexts: getTextSetter,
    setSelectedText,
    saveToHistory,
    updateFormattedText,
  });

  const handleLinkChange = createHandleLinkChange({
    text,
    setTexts: getTextSetter,
    setSelectedText,
    saveToHistory,
    updateFormattedText,
  });

  // Selection saver hook
  const { restoreSelection } = useSelectionSaver(text?.id);

  // Refs to store pending updates and timeout IDs
  const pendingContentUpdateRef = useRef(null);
  const pendingTaskUpdateRef = useRef(null);
  const pendingNoteUpdateRef = useRef(null);
  const contentTimeoutRef = useRef(null);
  const taskTimeoutRef = useRef(null);
  const noteTimeoutRef = useRef(null);

  // Store setters in refs to avoid dependency issues
  const setSelectedTextRef = useRef(setSelectedText);
  const setSelectedObjectRef = useRef(setSelectedObject);
  const getTextSetterRef = useRef(getTextSetter);
  const textIdRef = useRef(text?.id);

  // Update refs when props change
  useEffect(() => {
    setSelectedTextRef.current = setSelectedText;
    setSelectedObjectRef.current = setSelectedObject;
    getTextSetterRef.current = getTextSetter;
    textIdRef.current = text?.id;
  }, [setSelectedText, setSelectedObject, getTextSetter, text?.id]);

  // Content update function - debounced to update after user stops typing
  const debouncedContentUpdate = useCallback((htmlContent, plainText) => {
    const cleanedContent = htmlContent;
    const isHtml = /<\/?[a-z][\s\S]*?>/i.test(cleanedContent);

    // CRITICAL: Mark that there are pending changes so history will be saved on unselection
    if (isElementBeingEdited && typeof setPendingChanges === "function") {
      setPendingChanges(true);
      if (pendingChangesRef) {
        pendingChangesRef.current = true;
      }
    }

    // Store pending update in ref (no immediate state update)
    pendingContentUpdateRef.current = {
      content: cleanedContent,
      plainText,
      isHtml,
    };

    // Clear existing timeout
    if (contentTimeoutRef.current) {
      clearTimeout(contentTimeoutRef.current);
    }

    // Debounce: Update state after user stops typing for 50ms
    contentTimeoutRef.current = setTimeout(() => {
      if (pendingContentUpdateRef.current) {
        updateTextAndSelectedText({
          textId: textIdRef.current,
          setTexts: getTextSetterRef.current,
          setSelectedText: setSelectedTextRef.current,
          updates: {
            content: pendingContentUpdateRef.current.content,
            plainText: pendingContentUpdateRef.current.plainText,
            isHtml: pendingContentUpdateRef.current.isHtml,
            _lastUpdated: Date.now(),
          },
        });
        pendingContentUpdateRef.current = null;
      }
      contentTimeoutRef.current = null;
    }, 50); // 50ms after user stops typing
  }, [isElementBeingEdited, setPendingChanges, pendingChangesRef]); // Add dependencies

  // Task update function - debounced to update after user stops typing
  const debouncedTaskUpdate = useCallback((htmlContent, plainText) => {
    const cleanedContent = htmlContent.replace(/&nbsp;/g, " ");
    const isHtml = /<\/?[a-z][\s\S]*?>/i.test(cleanedContent);

    // CRITICAL: Mark that there are pending changes so history will be saved on unselection
    if (isElementBeingEdited && typeof setPendingChanges === "function") {
      setPendingChanges(true);
      if (pendingChangesRef) {
        pendingChangesRef.current = true;
      }
    }

    // Store pending update in ref (no immediate state update)
    pendingTaskUpdateRef.current = {
      task: htmlContent,
      taskPlainText: plainText,
      taskIsHtml: plainText ? isHtml : false,
    };

    // Clear existing timeout
    if (taskTimeoutRef.current) {
      clearTimeout(taskTimeoutRef.current);
    }

    // Debounce: Update state after user stops typing for 50ms
    taskTimeoutRef.current = setTimeout(() => {
      if (pendingTaskUpdateRef.current) {
        updateTextAndSelectedText({
          textId: textIdRef.current,
          setTexts: getTextSetterRef.current,
          setSelectedText: setSelectedTextRef.current,
          updates: {
            task: pendingTaskUpdateRef.current.task,
            taskPlainText: pendingTaskUpdateRef.current.taskPlainText,
            taskIsHtml: pendingTaskUpdateRef.current.taskIsHtml,
            _lastUpdated: Date.now(),
          },
        });
        pendingTaskUpdateRef.current = null;
      }
      taskTimeoutRef.current = null;
    }, 50); // 50ms after user stops typing
  }, [isElementBeingEdited, setPendingChanges, pendingChangesRef]); // Add dependencies

  // Store text data in refs for note updates to avoid stale closures
  const textDataRef = useRef({ selectedContent: text?.selectedContent, partsByType: text?.partsByType });
  useEffect(() => {
    textDataRef.current = {
      selectedContent: text?.selectedContent,
      partsByType: text?.partsByType,
    };
  }, [text?.selectedContent, text?.partsByType]);

  // Create debounced note handler with stable dependencies
  const noteContentChangeHandler = useMemo(
    () =>
      createNoteContentChangeHandler({
        text,
        setTexts: getTextSetter,
        setSelectedText,
        setPendingChanges,
        isElementBeingEdited,
      }),
    [text?.id, getTextSetter, setSelectedText, setPendingChanges, isElementBeingEdited]
  );

  // Store handler in ref
  const noteContentChangeHandlerRef = useRef(noteContentChangeHandler);
  useEffect(() => {
    noteContentChangeHandlerRef.current = noteContentChangeHandler;
  }, [noteContentChangeHandler]);

  // Store the note type (part vs regular) when editing starts to avoid stale closure issues
  const noteTypeRef = useRef(null);
  const selectedPartIdRef = useRef(null);

  // Update note type refs when note editing starts
  // CRITICAL: When showEditNote is true, always hide Edit Item Code section (belt-and-suspenders with handlePartClick)
  useEffect(() => {
    if (showEditNote) {
      noteTypeRef.current = text?.selectedContent?.type || null;
      selectedPartIdRef.current = text?.selectedContent?.id || null;
      setShowQtyUnitOptions(false);
    }
  }, [showEditNote, text?.selectedContent?.type, text?.selectedContent?.id]);

  // Note update function - debounced to update after user stops typing
  const debouncedNoteUpdate = useCallback(
    (htmlContent, plainText) => {
      // CRITICAL: Mark that there are pending changes so history will be saved on unselection
      if (isElementBeingEdited && typeof setPendingChanges === "function") {
        setPendingChanges(true);
        if (pendingChangesRef) {
          pendingChangesRef.current = true;
        }
      }

      // Store pending update with note type to ensure correct handling
      const isPartNote = noteTypeRef.current === "part";
      const partId = selectedPartIdRef.current;

      // Store pending update in ref (no immediate state update)
      pendingNoteUpdateRef.current = {
        note: htmlContent,
        plainText,
        isPartNote,
        partId,
      };

      // Clear existing timeout
      if (noteTimeoutRef.current) {
        clearTimeout(noteTimeoutRef.current);
      }

      // Debounce: Update state after user stops typing for 50ms
      noteTimeoutRef.current = setTimeout(() => {
        if (pendingNoteUpdateRef.current) {
          const { note, plainText, isPartNote: storedIsPartNote, partId: storedPartId } = pendingNoteUpdateRef.current;
          const currentTextData = textDataRef.current;

          // Create a handler with current state, ensuring correct note type handling
          const handler = createNoteContentChangeHandler({
            text: {
              ...currentTextData,
              id: textIdRef.current,
              selectedContent: storedIsPartNote && storedPartId
                ? { type: "part", id: storedPartId }
                : null,
            },
            setTexts: getTextSetterRef.current,
            setSelectedText: setSelectedTextRef.current,
            setPendingChanges,
            isElementBeingEdited,
          });

          handler(note, plainText);
          pendingNoteUpdateRef.current = null;
        }
        noteTimeoutRef.current = null;
      }, 50); // 50ms after user stops typing
    },
    [isElementBeingEdited, setPendingChanges, pendingChangesRef] // Add dependencies
  );

  // Cleanup timeouts and flush pending updates on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeouts
      if (contentTimeoutRef.current) {
        clearTimeout(contentTimeoutRef.current);
        contentTimeoutRef.current = null;
      }
      if (taskTimeoutRef.current) {
        clearTimeout(taskTimeoutRef.current);
        taskTimeoutRef.current = null;
      }
      if (noteTimeoutRef.current) {
        clearTimeout(noteTimeoutRef.current);
        noteTimeoutRef.current = null;
      }

      // Flush pending updates on unmount
      if (pendingContentUpdateRef.current) {
        updateTextAndSelectedText({
          textId: textIdRef.current,
          setTexts: getTextSetterRef.current,
          setSelectedText: setSelectedTextRef.current,
          updates: {
            content: pendingContentUpdateRef.current.content,
            plainText: pendingContentUpdateRef.current.plainText,
            isHtml: pendingContentUpdateRef.current.isHtml,
            _lastUpdated: Date.now(),
          },
        });
        pendingContentUpdateRef.current = null;
      }
      if (pendingTaskUpdateRef.current) {
        updateTextAndSelectedText({
          textId: textIdRef.current,
          setTexts: getTextSetterRef.current,
          setSelectedText: setSelectedTextRef.current,
          updates: {
            task: pendingTaskUpdateRef.current.task,
            taskPlainText: pendingTaskUpdateRef.current.taskPlainText,
            taskIsHtml: pendingTaskUpdateRef.current.taskIsHtml,
            _lastUpdated: Date.now(),
          },
        });
        pendingTaskUpdateRef.current = null;
      }
      if (pendingNoteUpdateRef.current) {
        const { note, plainText, isPartNote: storedIsPartNote, partId: storedPartId } = pendingNoteUpdateRef.current;
        const currentTextData = textDataRef.current;

        // Create a handler with current state to ensure correct note type handling
        const handler = createNoteContentChangeHandler({
          text: {
            ...currentTextData,
            id: textIdRef.current,
            selectedContent: storedIsPartNote && storedPartId
              ? { type: "part", id: storedPartId }
              : null,
          },
          setTexts: getTextSetterRef.current,
          setSelectedText: setSelectedTextRef.current,
        });

        handler(note, plainText);
        pendingNoteUpdateRef.current = null;
      }
    };
  }, []); // Empty deps - using refs for all values

  // Safety check: if text was deleted, clear selection and close component
  // Check if text exists in either main canvas or offcanvas texts arrays
  // This must be before the early return to follow React hooks rules
  useEffect(() => {
    if (text?.id) {
      const textExists =
        (texts && texts.some((t) => t.id === text.id)) ||
        (offCanvasTexts && offCanvasTexts.some((t) => t.id === text.id));

      if (!textExists) {
        // Text was deleted, clear selection and close component
        setSelectedText(null);
        setSelectedTextId(null);
        setSelectedObject(null);
        setShowTextBox(false);
        if (setSelectedObjects) {
          setSelectedObjects([]);
        }
      }
    }
  }, [text?.id, texts, offCanvasTexts, setSelectedText, setSelectedTextId, setSelectedObject, setShowTextBox, setSelectedObjects]);

  // After all hooks – if no text, render nothing
  if (!text) return null;

  const xInputHandlers = createNumberInputHandler({
    field: "x",
    min: MIN_X,
    max: dynamicMaxX,
    textId: text.id,
    setTexts: getTextSetter,
    setSelectedText,
    setLocalInputValues,
    saveToHistory,
    setPendingChanges,
    pendingChangesRef,
  });

  const yInputHandlers = createNumberInputHandler({
    field: "y",
    min: MIN_Y,
    max: dynamicMaxY,
    textId: text.id,
    setTexts: getTextSetter,
    setSelectedText,
    setLocalInputValues,
    saveToHistory,
    setPendingChanges,
    pendingChangesRef,
  });
  const yInputHandlersWithDebug = {
    ...yInputHandlers,
    onChange: (e) => {
      yInputHandlers.onChange(e);
    },
    onBlur: (e) => {
      yInputHandlers.onBlur(e);
    },
  };

  const widthInputHandlers = createNumberInputHandler({
    field: "width",
    min: MIN_WIDTH,
    max: dynamicMaxWidth,
    textId: text.id,
    setTexts: getTextSetter,
    setSelectedText,
    setLocalInputValues,
    saveToHistory,
    setPendingChanges,
    pendingChangesRef,
  });

  const strokeWidthInputHandlers = createNumberInputHandler({
    field: "strokeWidth",
    min: 0,
    max: MAX_STROKE_WIDTH,
    textId: text.id,
    setTexts: getTextSetter,
    setSelectedText,
    setLocalInputValues,
    saveToHistory,
    setPendingChanges,
    pendingChangesRef,
  });

  return (
    <>
      <div
        id={id}
        className="textbox-with-buttons"
        style={{ pointerEvents: "auto" }}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.(e);
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="interactive-content">
          {/* Expand/Collapse Button */}
          <ExpandCollapseHeader
            title={text.name}
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded((prev) => !prev)}
          />

          {isExpanded &&
            !editTextBoxContent &&
            !showEditTask &&
            !text.selectedContent &&
            !showEditNote && (
              <div style={{ padding: "16px" }}>
                {/* X and Y Position (Center Coordinates) */}
                <PositionControls
                  xValue={localInputValues.x}
                  yValue={localInputValues.y}
                  minX={MIN_X}
                  maxX={dynamicMaxX}
                  minY={MIN_Y}
                  maxY={dynamicMaxY}
                  onXChange={xInputHandlers.onChange}
                  onYChange={yInputHandlersWithDebug.onChange}
                  onXBlur={xInputHandlers.onBlur}
                  onYBlur={yInputHandlersWithDebug.onBlur}
                  onXKeyDown={xInputHandlers.onKeyDown}
                  onYKeyDown={yInputHandlersWithDebug.onKeyDown}
                />

                {/* Width */}
                <WidthControl
                  value={localInputValues.width}
                  min={MIN_WIDTH}
                  max={dynamicMaxWidth}
                  onChange={widthInputHandlers.onChange}
                  onBlur={widthInputHandlers.onBlur}
                  onKeyDown={widthInputHandlers.onKeyDown}
                />

                {/* Stroke Size */}
                <StrokeSizeControl
                  value={localInputValues.strokeWidth}
                  min={0}
                  max={MAX_STROKE_WIDTH}
                  onChange={strokeWidthInputHandlers.onChange}
                  onBlur={strokeWidthInputHandlers.onBlur}
                  onKeyDown={strokeWidthInputHandlers.onKeyDown}
                />

                {/* Show Background */}
                <BackgroundSettings
                  showBackground={text.showBackground}
                  backgroundColor={text.backgroundColor}
                  onShowBackgroundChange={(value) => {
                    updateTextAndSelectedText({
                      textId: text.id,
                      setTexts: getTextSetter,
                      setSelectedText,
                      updates: { showBackground: value },
                    });
                    saveToHistory("update_text_background");
                  }}
                  onColorChange={(newColor) => {
                    updateTextAndSelectedText({
                      textId: text.id,
                      setTexts: getTextSetter,
                      setSelectedText,
                      updates: { backgroundColor: newColor },
                    });
                    saveToHistory("update_text_background");
                  }}
                />

                {/* Font Size Buttons */}
                <FontSizeButtons
                  currentSize={fontSize}
                  onSizeChange={(size) => {
                    setFontSize(size);
                    const fontSizeValue =
                      size === "small" ? 11 : size === "medium" ? 14 : 18;
                    updateTextAndSelectedText({
                      textId: text.id,
                      setTexts: getTextSetter,
                      setSelectedText,
                      updates: { fontSize: fontSizeValue },
                    });
                    saveToHistory();
                  }}
                />

                {/* Edit Content Button */}
                <EditContentButton
                  onClick={() => {
                    setEditTextBoxContent(true);
                    setShowEditNote(false);
                    setShowEditTask(false);
                    updateTextAndSelectedText({
                      textId: text.id,
                      setTexts: getTextSetter,
                      setSelectedText,
                      updates: { showContentInput: false },
                    });
                  }}
                />

                {/* Delete Button */}
                <DeleteButton
                  onDelete={() => {
                    if (text?.id) {
                      // Check if this text is in the main canvas or offcanvas
                      const isInMainCanvas =
                        texts && texts.some((t) => t.id === text.id);
                      const isInOffCanvas =
                        offCanvasTexts &&
                        offCanvasTexts.some((t) => t.id === text.id);

                      if (isInMainCanvas) {
                        // Delete from main canvas
                        setTexts((prev) =>
                          prev.filter((t) => t.id !== text.id)
                        );
                        if (setRemovedTexts) {
                          setRemovedTexts((prev) => [...prev, text.id]);
                        }
                      } else if (isInOffCanvas) {
                        // Delete from offcanvas
                        setOffCanvasTexts((prev) =>
                          prev.filter((t) => t.id !== text.id)
                        );
                        if (setRemovedOffCanvasTexts) {
                          setRemovedOffCanvasTexts((prev) => [
                            ...prev,
                            text.id,
                          ]);
                        }
                      }
                    }

                    // Clear all selection state immediately to close the component
                    setSelectedText(null);
                    setSelectedTextId(null);
                    setSelectedObject(null);
                    setShowTextBox(false);

                    // Also clear selectedObjects if it exists (for multi-select)
                    if (setSelectedObjects) {
                      setSelectedObjects([]);
                    }

                    // Clear transformer nodes to remove blue handles
                    if (transformerRef?.current) {
                      transformerRef.current.nodes([]);
                      if (transformerRef.current.getLayer()) {
                        transformerRef.current.getLayer().batchDraw();
                      }
                    }

                    saveToHistory();
                  }}
                />
              </div>
            )}

          {isExpanded &&
            editTextBoxContent &&
            !showEditTask &&
            !showEditNote &&
            !text.selectedContent && (
              <div>
                <BackButton
                  label="Edit Text Box Content"
                  onClick={() => {
                    setEditTextBoxContent(false);
                  }}
                />

                <TextboxContentFormatter
                  selectedColor={selectedColor}
                  handleColorChange={handleColorChange}
                  handleUnderlineChange={handleUnderlineChange}
                  handleNumberedList={handleNumberedList}
                  handleBulletedList={handleBulletedList}
                  handleHighlightChange={handleContentHighlightChange}
                  handleLinkChange={handleLinkChange}
                />
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                  }}
                >
                  <LexicalTextEditor
                    id={`content-div-${text.id}`}
                    content={text.content || ""}
                    fontSize={contentFontSize}
                    fontFamily={text.fontFamily || "Roboto"}
                    onKeyDown={handleListKeyDown}
                    style={{ marginBottom: "8px" }}
                    color={text.contentColor}
                    underline={text.contentUnderline}
                    highlight={text.contentHighlight}
                    {...createLexicalEditorHandlers({
                      field: "content",
                      textId: text.id,
                      setTexts: getTextSetter,
                      setSelectedText,
                      saveToHistory,
                      shouldSaveHistory: false,
                      setPendingChanges,
                      isElementBeingEdited,
                    })}
                    // Override onContentChange with our debounced version (spread first, then override)
                    onContentChange={debouncedContentUpdate}
                    onBlur={() => {
                      // Clear any pending timeout and flush immediately on blur
                      if (contentTimeoutRef.current) {
                        clearTimeout(contentTimeoutRef.current);
                        contentTimeoutRef.current = null;
                      }

                      // Flush any pending updates on blur
                      if (pendingContentUpdateRef.current) {
                        updateTextAndSelectedText({
                          textId: textIdRef.current,
                          setTexts: getTextSetterRef.current,
                          setSelectedText: setSelectedTextRef.current,
                          updates: {
                            content: pendingContentUpdateRef.current.content,
                            plainText: pendingContentUpdateRef.current.plainText,
                            isHtml: pendingContentUpdateRef.current.isHtml,
                            _lastUpdated: Date.now(),
                          },
                        });
                        pendingContentUpdateRef.current = null;
                      }
                      saveToHistory();
                    }}
                  />
                </div>

                <div
                  style={{
                    width: "100%",
                    height: BUTTON_HEIGHT,
                    backgroundColor:
                      hoveredButton === "part"
                        ? BUTTON_HOVER_COLOR
                        : BUTTON_COLOR,
                    borderRadius: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "10px",
                    marginTop: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (!selectedTextId) {
                      // Calculate top-left coordinates from desired center position
                      const desiredCenterX = 200;
                      const desiredCenterY = 200;
                      const textWidth = 225;
                      const textHeight = 100;
                      const topLeftX = desiredCenterX - textWidth / 2;
                      const topLeftY = desiredCenterY - textHeight / 2;

                      const newText = {
                        id: uuidv4(),
                        x: topLeftX,
                        y: topLeftY,
                        part: "",
                        note: "",
                        task: "",
                        textStyles: {},
                        width: textWidth,
                        height: textHeight,
                        createdOnCanvas: true,
                      };

                      setSelectedTextId(newText.id);

                      setTexts((prevTexts) => [...prevTexts, newText]);

                      // saveToHistory();
                    }

                    setPartsModalOpen(true);
                  }}
                  onMouseEnter={() => setHoveredButton("part")}
                  onMouseLeave={() => setHoveredButton(null)}
                >
                  <span
                    style={{
                      color: TEXT_COLOR,
                      fontWeight: "bold",
                      fontSize: "14px",
                    }}
                  >
                    + Item Code & Description
                  </span>
                </div>

                <EditItemCodeButton
                  hasParts={Object.keys(text.partsByType || {}).some(
                    (key) => text.partsByType[key]?.parts?.length > 0
                  )}
                  onClick={() => {
                    const sortedTypeKeys = Object.entries(
                      text.partsByType || {}
                    )
                      .filter(([_, value]) => value?.parts?.length > 0)
                      .sort(
                        (a, b) => (a[1].order || 9999) - (b[1].order || 9999)
                      );

                    const [firstTypeKey, firstTypeData] =
                      sortedTypeKeys[0] || [];

                    if (!firstTypeKey || !firstTypeData) return;

                    const sortedParts = firstTypeData.parts
                      .slice()
                      .sort((a, b) => {
                        const partA = a.part?.match(/\(([^)]+)\)/)?.[1] || "";
                        const partB = b.part?.match(/\(([^)]+)\)/)?.[1] || "";
                        return partA.localeCompare(partB, undefined, {
                          numeric: true,
                          sensitivity: "base",
                        });
                      });

                    const firstPart = sortedParts[0];

                    if (firstPart) {
                      const newSelectedContent = {
                        type: "part",
                        part: firstPart,
                        typeKey: firstTypeKey,
                        qty: firstPart.qty,
                        unit: firstPart.unit,
                        part_ref: firstPart.part_ref,
                        id: firstPart.id,
                        unit_type_id: firstPart.unit_type_id,
                      };

                      // Prevent the useEffect from auto-showing qty/unit panel
                      // by updating the ref before setting selectedContent
                      prevSelectedPartIdRef.current = firstPart.id;

                      // Explicitly hide qty/unit panel to show Edit/Remove buttons instead
                      setShowQtyUnitOptions(false);

                      getTextSetter((prevTexts) =>
                        prevTexts.map((t) =>
                          t.id === text.id
                            ? { ...t, selectedContent: newSelectedContent }
                            : t
                        )
                      );

                      setSelectedText((prevText) => ({
                        ...prevText,
                        selectedContent: newSelectedContent,
                      }));

                      // saveToHistory();
                      setEditTextBoxContent(false);
                    }
                  }}
                />

                <SectionButtons
                  hasTask={!!text?.task}
                  hasNote={!!text?.note}
                  savedNote={savedNote}
                  onTaskClick={() => {
                    // Ensure we have a valid text entry first if this is a new text
                    if (!selectedTextId) {
                      // Calculate top-left coordinates from desired center position
                      const desiredCenterX = 200;
                      const desiredCenterY = 200;
                      const textWidth = 225;
                      const textHeight = 100;
                      const topLeftX = desiredCenterX - textWidth / 2;
                      const topLeftY = desiredCenterY - textHeight / 2;

                      const newText = {
                        id: uuidv4(),
                        x: topLeftX,
                        y: topLeftY,
                        part: "",
                        note: "",
                        task: "",
                        textStyles: {},
                        width: textWidth,
                        height: textHeight,
                        createdOnCanvas: true,
                      };

                      setSelectedTextId(newText.id);
                      setSelectedText(newText);
                      setTexts((prevTexts) => [...prevTexts, newText]);
                      // saveToHistory();
                    }

                    setShowEditTask(true);
                    setEditTextBoxContent(false);
                  }}
                  onNoteClick={() => {
                    // Ensure we have a valid text entry first if this is a new text
                    if (!selectedTextId) {
                      // Calculate top-left coordinates from desired center position
                      const desiredCenterX = 200;
                      const desiredCenterY = 200;
                      const textWidth = 225;
                      const textHeight = 100;
                      const topLeftX = desiredCenterX - textWidth / 2;
                      const topLeftY = desiredCenterY - textHeight / 2;

                      const newText = {
                        id: uuidv4(),
                        x: topLeftX,
                        y: topLeftY,
                        part: "",
                        note: "",
                        task: "",
                        textStyles: {},
                        width: textWidth,
                        height: textHeight,
                        createdOnCanvas: true,
                      };

                      setSelectedTextId(newText.id);
                      setSelectedText(newText);
                      setTexts((prevTexts) => [...prevTexts, newText]);
                      // saveToHistory();
                    }

                    setShowEditNote(true);
                    setEditTextBoxContent(false);
                  }}
                  hoveredButton={hoveredButton}
                  onMouseEnter={setHoveredButton}
                  onMouseLeave={() => setHoveredButton(null)}
                />
              </div>
            )}

          {isExpanded &&
            !editTextBoxContent &&
            !showEditTask &&
            showEditNote && (
              <div>
                <BackButton
                  label="Edit Note"
                  onClick={() => {
                    setEditTextBoxContent(true);
                    setShowEditNote(false);
                    setShowEditTask(false);
                    updateTextAndSelectedText({
                      textId: text.id,
                      setTexts: getTextSetter,
                      setSelectedText,
                      updates: { selectedContent: null },
                    });
                  }}
                />

                <TextboxContentFormatter
                  selectedColor={selectedColor}
                  handleColorChange={(newColor) => {
                    // Use Lexical editor's global function for color change
                    if (window.handleLexicalColorChange) {
                      window.handleLexicalColorChange(newColor);
                    }
                  }}
                  handleUnderlineChange={() => {
                    // Use Lexical editor's global function for underline change
                    if (window.handleLexicalUnderlineChange) {
                      window.handleLexicalUnderlineChange();
                    }
                  }}
                  handleHighlightChange={() => {
                    // Use Lexical editor's global function for highlight change
                    if (window.handleLexicalHighlightChange) {
                      window.handleLexicalHighlightChange();
                    }
                  }}
                  handleNumberedList={() => {
                    // Use Lexical editor's global function for numbered list
                    if (window.handleLexicalNumberedList) {
                      window.handleLexicalNumberedList();
                    }
                  }}
                  handleBulletedList={() => {
                    // Use Lexical editor's global function for bulleted list
                    if (window.handleLexicalBulletedList) {
                      window.handleLexicalBulletedList();
                    }
                  }}
                  handleLinkChange={() => {
                    // Use Lexical editor's global function for link change
                    if (window.handleLexicalLinkChange) {
                      window.handleLexicalLinkChange();
                    }
                  }}
                />

                <div
                  style={{
                    position: "relative",

                    width: "100%",
                  }}
                >
                  {/* Replace contentEditable div with LexicalTextEditor */}
                  <LexicalTextEditor
                    key={`note-editor-${text.id}-${text.selectedContent?.id || 'none'}`}
                    id={`note-div-${text.id}`}
                    content={getNoteContent(text)}
                    fontSize={noteFontSize}
                    fontFamily={text.fontFamily || "Roboto"}
                    onKeyDown={handleListKeyDown}
                    style={{ marginBottom: "8px" }}
                    color={text.noteColor}
                    underline={text.noteUnderline}
                    highlight={text.noteHighlighted}
                    onBlur={() => {
                      // Clear any pending timeout and flush immediately on blur
                      if (noteTimeoutRef.current) {
                        clearTimeout(noteTimeoutRef.current);
                        noteTimeoutRef.current = null;
                      }

                      // Flush any pending updates on blur
                      if (pendingNoteUpdateRef.current) {
                        const { note, plainText, isPartNote: storedIsPartNote, partId: storedPartId } = pendingNoteUpdateRef.current;
                        const currentTextData = textDataRef.current;

                        // Create a handler with current state to ensure correct note type handling
                        const handler = createNoteContentChangeHandler({
                          text: {
                            ...currentTextData,
                            id: textIdRef.current,
                            selectedContent: storedIsPartNote && storedPartId
                              ? { type: "part", id: storedPartId }
                              : null,
                          },
                          setTexts: getTextSetterRef.current,
                          setSelectedText: setSelectedTextRef.current,
                        });

                        handler(note, plainText);
                        pendingNoteUpdateRef.current = null;
                      }
                      if (shouldSaveNoteHistory(text)) {
                        saveToHistory();
                      }
                    }}
                    onContentChange={debouncedNoteUpdate}
                    onColorChange={createNoteColorChangeHandler({
                      text,
                      setTexts: getTextSetter,
                      setSelectedText,
                      saveToHistory,
                    })}
                    onUnderlineChange={createNoteUnderlineChangeHandler({
                      text,
                      setTexts: getTextSetter,
                      setSelectedText,
                      saveToHistory,
                    })}
                    onHighlightChange={createNoteHighlightChangeHandler({
                      text,
                      setTexts: getTextSetter,
                      setSelectedText,
                      saveToHistory,
                    })}
                    onNumberedList={() => {
                      updateTextAndSelectedText({
                        textId: text.id,
                        setTexts: getTextSetter,
                        setSelectedText,
                        updates: { _lastUpdated: Date.now() },
                      });
                      saveToHistory();
                    }}
                    onBulletedList={() => {
                      updateTextAndSelectedText({
                        textId: text.id,
                        setTexts: getTextSetter,
                        setSelectedText,
                        updates: { _lastUpdated: Date.now() },
                      });
                      saveToHistory();
                    }}
                    onLinkChange={createNoteLinkChangeHandler({
                      text,
                      setTexts: getTextSetter,
                      setSelectedText,
                      saveToHistory,
                    })}
                  />
                </div>

                {/* Delete Button */}
                <RemoveNoteButton
                  onClick={() => {
                    // Clear any pending note updates before deleting
                    pendingNoteUpdateRef.current = null;

                    if (
                      showPartNoteEdit ||
                      text?.selectedContent?.type === "part"
                    ) {
                      handleDeleteNotForPart();
                    } else {
                      updateTextAndSelectedText({
                        textId: text.id,
                        setTexts: getTextSetter,
                        setSelectedText,
                        updates: { note: null, selectedContent: null },
                      });
                      saveToHistory();
                    }

                    setShowEditNote(false);

                    setEditTextBoxContent(true);
                  }}
                />
              </div>
            )}

          {isExpanded &&
            !editTextBoxContent &&
            !showEditNote &&
            showEditTask && (
              <div>
                <BackButton
                  label="Edit Task"
                  onClick={() => {
                    setEditTextBoxContent(true);
                    setShowEditNote(false);
                    setShowEditTask(false);
                    updateTextAndSelectedText({
                      textId: text.id,
                      setTexts: getTextSetter,
                      setSelectedText,
                      updates: { selectedContent: null },
                    });
                  }}
                />

                <TextboxContentFormatter
                  selectedColor={selectedColor}
                  handleColorChange={(newColor) => {
                    // Use Lexical editor's global function for color change
                    if (window.handleLexicalColorChange) {
                      window.handleLexicalColorChange(newColor);
                    }
                  }}
                  handleUnderlineChange={() => {
                    // Use Lexical editor's global function for underline change
                    if (window.handleLexicalUnderlineChange) {
                      window.handleLexicalUnderlineChange();
                    }
                  }}
                  handleNumberedList={() => {
                    // Use Lexical editor's global function for numbered list
                    if (window.handleLexicalNumberedList) {
                      window.handleLexicalNumberedList();
                    }
                  }}
                  handleHighlightChange={() => {
                    // Use Lexical editor's global function for highlight change
                    if (window.handleLexicalHighlightChange) {
                      window.handleLexicalHighlightChange();
                    }
                  }}
                  handleBulletedList={() => {
                    // Use Lexical editor's global function for bulleted list
                    if (window.handleLexicalBulletedList) {
                      window.handleLexicalBulletedList();
                    }
                  }}
                  handleLinkChange={() => {
                    // Use Lexical editor's global function for link change
                    if (window.handleLexicalLinkChange) {
                      window.handleLexicalLinkChange();
                    }
                  }}
                />

                <div
                  style={{
                    position: "relative",

                    width: "100%",
                  }}
                >
                  {/* Replace contentEditable div with LexicalTextEditor */}
                  <LexicalTextEditor
                    id={`task-div-${text.id}`}
                    content={text.task || ""}
                    fontSize={taskFontSize}
                    fontFamily={text.fontFamily || "Roboto"}
                    onKeyDown={handleListKeyDown}
                    style={{ marginBottom: "8px" }}
                    color={text.taskColor}
                    underline={text.taskUnderline}
                    highlight={text.taskHighlight}
                    {...createLexicalEditorHandlers({
                      field: "task",
                      textId: text.id,
                      setTexts: getTextSetter,
                      setSelectedText,
                      saveToHistory,
                      shouldSaveHistory: true,
                      setPendingChanges,
                      isElementBeingEdited,
                    })}
                    // Override onContentChange with our debounced version (spread first, then override)
                    onContentChange={debouncedTaskUpdate}
                    onBlur={() => {
                      // Clear any pending timeout and flush immediately on blur
                      if (taskTimeoutRef.current) {
                        clearTimeout(taskTimeoutRef.current);
                        taskTimeoutRef.current = null;
                      }

                      // Flush any pending updates on blur
                      if (pendingTaskUpdateRef.current) {
                        updateTextAndSelectedText({
                          textId: textIdRef.current,
                          setTexts: getTextSetterRef.current,
                          setSelectedText: setSelectedTextRef.current,
                          updates: {
                            task: pendingTaskUpdateRef.current.task,
                            taskPlainText: pendingTaskUpdateRef.current.taskPlainText,
                            taskIsHtml: pendingTaskUpdateRef.current.taskIsHtml,
                            _lastUpdated: Date.now(),
                          },
                        });
                        pendingTaskUpdateRef.current = null;
                      }
                      saveToHistory();
                    }}
                  />
                </div>
                {/* Delete Button */}
                <div style={{ marginTop: "16px" }}>
                  <button
                    onClick={() => {
                      updateTextAndSelectedText({
                        textId: text.id,
                        setTexts: getTextSetter,
                        setSelectedText,
                        updates: { task: null, selectedContent: null },
                      });
                      saveToHistory();
                      setShowEditTask(false);
                      setEditTextBoxContent(true);
                    }}
                    style={{
                      width: "100%",
                      height: BUTTON_HEIGHT,
                      backgroundColor: "white",
                      borderRadius: "5px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      border: "2px solid red",
                      color: "red",
                    }}
                  >
                    Remove Task
                  </button>
                </div>
              </div>
            )}

          {isExpanded &&
            showQtyUnitOptions &&
            text.selectedContent &&
            !showEditNote && (
            <div>
              <button
                style={{
                  background: "transparent",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px", // space between icon and text
                  cursor: "pointer",
                  padding: "4px 8px",
                }}
                onClick={() => {
                  setShowQtyUnitOptions(false);
                }}
              >
                <svg
                  viewBox="6 4 16 16"
                  width="24"
                  height="24"
                  style={{ color: "black", display: "block" }}
                >
                  <path
                    d="M10 12h10M10 12l4-4M10 12l4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Edit Item Code</span>
              </button>
              <QuantityUnitControls
                quantity={text.selectedContent?.qty?.toString() ?? ""}
                unit={text.selectedContent?.unit || ""}
                unitOptions={filteredUnits}
                onQuantityChange={handleQtyChange}
                onUnitChange={handleUnitChange}
                autoFocus={shouldAutoFocusQty}
              />

              <PartActionButtons
                onReplaceClick={() => {
                  const selectedId = text?.selectedContent?.id;
                  let type = {};
                  Object.entries(text.partsByType || {}).forEach(
                    ([_, typeData]) => {
                      typeData.parts.forEach((partObj) => {
                        // Skip null or undefined partObj entries
                        if (!partObj) return;

                        if (partObj?.id === selectedId) {
                          type = text.partsByType;
                        }
                      });
                    }
                  );

                  getTextSetter((prevTexts) =>
                    prevTexts.map((t) =>
                      t.id !== selectedTextId
                        ? t
                        : {
                          ...t,
                          selectedContent: {
                            ...t.selectedContent,
                            part: {
                              ...t.selectedContent?.part,
                              type,
                            },
                          },
                        }
                    )
                  );
                  setSelectedText((prev) => ({
                    ...prev,
                    selectedContent: {
                      ...prev.selectedContent,
                      part: {
                        ...prev.selectedContent.part,
                        type,
                      },
                    },
                  }));
                  setPartsModalOpen(true);
                }}
                onRemoveClick={() => handleRemovePart()}
              />
            </div>
          )}

          {isExpanded &&
            (text?.selectedContent?.type === "part" ||
              text?.selectedContent?.part) &&
            !showQtyUnitOptions &&
            !showEditNote && (
              <div>
                <BackButton
                  label={`Item Code ${selectedPartName.match(/\(([^)]+)\)/)?.[1] || ""
                    } Selected`}
                  onClick={() => {
                    setSelectedText((prevText) => ({
                      ...prevText,
                      selectedContent: null,
                    }));
                    getTextSetter((prevTexts) =>
                      prevTexts.map((t) =>
                        t.id === text.id ? { ...t, selectedContent: null } : t
                      )
                    );
                  }}
                />

                <NavigationToolbar
                  onHighlightClick={handleHighlightChange}
                  onPrevClick={handlePrevPart}
                  onNextClick={handleNextPart}
                  hasPrev={hasPrevPart}
                  hasNext={hasNextPart}
                />

                <PartActionButtons
                  onReplaceClick={() => {
                    setShowQtyUnitOptions(true);
                  }}
                  onRemoveClick={() => handleRemovePart()}
                  // primaryLabel={
                  //   !showPartNoteEdit ? "Edit Item" : "Replace Item"
                  // }
                  primaryLabel="Edit Item"
                />

                <div
                  style={{
                    height: "30px", // or use BUTTON_HEIGHT variable
                    backgroundColor: "white",
                    borderRadius: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    border: "2px solid #0056b3",
                    color: "#0056b3",
                    marginTop: "15px",
                  }}
                  onClick={() => {
                    setShowEditNote(true);
                    addNoteBelowPart();

                    setShowEditTask(false);
                    setEditTextBoxContent(false);
                  }}
                  onMouseEnter={() => setHoveredButton("removePart")}
                  onMouseLeave={() => setHoveredButton(null)}
                >
                  {showPartNoteEdit ? "Edit Note" : "+ Note Below Item"}
                </div>
              </div>
            )}
        </div>
      </div>
    </>
  );
};

// Memoize component to prevent unnecessary re-renders
// Only re-render when props actually change
const MemoizedTextBoxWithButtons = React.memo(TextBoxWithButtons, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  // Returns true if props are equal (skip re-render), false if different (re-render)

  // Check if text ID changed (different text element)
  if (prevProps.text?.id !== nextProps.text?.id) {
    return false; // Different text, re-render
  }

  // Check if partsByType reference changed (for part notes)
  const partsByTypeChanged =
    prevProps.text?.partsByType !== nextProps.text?.partsByType;

  // If partsByType reference changed, re-render
  if (partsByTypeChanged) {
    return false; // Props are different, re-render
  }

  // Check if _lastUpdated changed (indicates debounced state update)
  // Only re-render if it's a significant change (not just visual update)
  const lastUpdatedChanged =
    prevProps.text?._lastUpdated !== nextProps.text?._lastUpdated;

  // If _lastUpdated changed AND content actually changed, re-render
  if (lastUpdatedChanged) {
    const contentChanged = prevProps.text?.content !== nextProps.text?.content;
    const taskChanged = prevProps.text?.task !== nextProps.text?.task;
    const noteChanged = prevProps.text?.note !== nextProps.text?.note;

    if (contentChanged || taskChanged || noteChanged) {
      return false; // Actual content changed, re-render
    }
    // _lastUpdated changed but content didn't - skip re-render (visual-only update)
  }

  // Check if position (x, y) changed - CRITICAL for real-time position updates during drag
  const positionChanged =
    prevProps.text?.x !== nextProps.text?.x ||
    prevProps.text?.y !== nextProps.text?.y;

  // If position changed, re-render to update position inputs
  if (positionChanged) {
    return false; // Props are different, re-render
  }

  // Check if width/strokeWidth changed - CRITICAL so WidthControl shows updated value after transform/resize
  const dimensionsChanged =
    prevProps.text?.width !== nextProps.text?.width ||
    prevProps.text?.strokeWidth !== nextProps.text?.strokeWidth;

  if (dimensionsChanged) {
    return false; // Props are different, re-render
  }

  // Check all other critical props
  const propsEqual = (
    prevProps.text?.id === nextProps.text?.id &&
    prevProps.text?.content === nextProps.text?.content &&
    prevProps.text?.task === nextProps.text?.task &&
    prevProps.text?.note === nextProps.text?.note &&
    prevProps.text?.selectedContent?.id === nextProps.text?.selectedContent?.id &&
    prevProps.text?.selectedContent?.type === nextProps.text?.selectedContent?.type &&
    prevProps.text?.selectedContent?.qty === nextProps.text?.selectedContent?.qty &&
    prevProps.text?.selectedContent?.unit === nextProps.text?.selectedContent?.unit &&
    prevProps.showEditTask === nextProps.showEditTask &&
    prevProps.showEditNote === nextProps.showEditNote &&
    prevProps.showQtyUnitOptions === nextProps.showQtyUnitOptions &&
    prevProps.editTextBoxContent === nextProps.editTextBoxContent &&
    prevProps.selectedTextId === nextProps.selectedTextId &&
    prevProps.canvasHeight === nextProps.canvasHeight &&
    prevProps.isElementBeingEdited === nextProps.isElementBeingEdited &&
    prevProps.text?.fontSize === nextProps.text?.fontSize &&
    prevProps.text?.showBackground === nextProps.text?.showBackground &&
    prevProps.text?.backgroundColor === nextProps.text?.backgroundColor
  );

  // Return true if props are equal (skip re-render), false if different (re-render)
  return propsEqual;
});

export default MemoizedTextBoxWithButtons;
export { setTextBoxDeselecting };
